import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { declaraEdad, entrarConCodigo, formatApiErrorDetail, pidePermisoTutor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PreguntaDeEdad from "@/components/PreguntaDeEdad";
import AALogo from "@/components/AALogo";

/**
 * La pantalla de quien todavía no puede usar la Academia, y lo que puede hacer para poder.
 *
 * Son dos situaciones y no una:
 *
 *  · `sin_declarar` — cuentas de antes de que esto se preguntara. Contestan y siguen.
 *  · `espera_tutor` — menor de catorce sin permiso. Aquí está el problema de verdad: hay
 *    padres que tardan días en abrir un correo, o no lo abren. Un alumno mirando una
 *    pantalla que dice «espera» sin nada que hacer se va y no vuelve.
 *
 * Por eso esta pantalla ofrece TRES salidas y no una: reenviar el aviso, **corregir el
 * correo** —un correo mal tecleado es la causa más común de que un permiso no llegue nunca—
 * y el **código de clase**, que resuelve el permiso en el acto si su centro tiene las
 * autorizaciones recogidas. Con las tres delante, esperar deja de ser lo único que se puede
 * hacer.
 *
 * Y SE VUELVE A PREGUNTAR AL SERVIDOR, PORQUE ESTA PANTALLA LA CIERRA OTRA PERSONA. Es lo que
 * la separa de todas las demás: lo que hace falta para que desaparezca no lo hace quien la
 * está mirando, lo hace su padre o su madre en otro aparato. Y el usuario que decide si se
 * enseña —`ProtectedRoute`, con `user.edad_estado`— es el que se leyó al abrir la sesión, así
 * que **el permiso puede estar dado desde hace un minuto y aquí seguir diciendo que falta**.
 *
 * Gabri lo cazó el 4 de octubre de 2026 recorriendo el camino entero: verificó, entró, pulsó el
 * mundo 3D y *«me ha vuelto a aparecer el mensaje de enviar correo al tutor; he refrescado y
 * entonces sí se me ha abierto»*. Recargar lo arreglaba, pero quien no sabe que hay que
 * recargar hace lo que invita la pantalla: **darle otra vez a enviar**, y entonces al tutor le
 * llega un segundo correo diciéndole que la cuenta está parada esperando un permiso que acaba
 * de dar. Eso se cierra en el servidor —`modules/auth/tutor.py` ya no manda un permiso que
 * consta— y se cierra aquí, que es donde además se arregla solo.
 *
 * TRES MOMENTOS Y NINGUNO ES UN RELOJ RÁPIDO: al montar, al volver a la pestaña —que es el
 * caso de verdad: el alumno se va al correo, o mira el móvil de su madre, y vuelve— y un
 * repaso lento de medio minuto para el que se queda delante mientras su padre pulsa. No se
 * pregunta más seguido a propósito: el backend vive en el plan Free de Render, con 0,1 de CPU.
 */
/** Cada cuánto se vuelve a preguntar mientras se espera al tutor. Medio minuto: lo bastante
 *  para que quien está delante vea abrirse la puerta sola, y lo bastante poco para no castigar
 *  a un servidor de 0,1 de CPU. Quien se va y vuelve no espera a esto: lo coge el `focus`. */
const CADA_CUANTO = 30000;

// El enlace para casa (B3.1), diferido: esta puerta va en la primera carga y el generador de QR
// solo lo necesita quien espera el permiso y decide pulsar.
const EnlaceParaCasa = lazy(() => import("@/components/EnlaceParaCasa"));

export default function PuertaDeEdad() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [edad, setEdad] = useState({ anio: "", mes: "" });
  const [tutor, setTutor] = useState(user?.tutor_email || "");
  const [codigo, setCodigo] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const esperando = user?.edad_estado === "espera_tutor";

  useEffect(() => {
    if (!esperando) return undefined;
    const vuelveAPreguntar = () => { if (!document.hidden) refresh(); };
    vuelveAPreguntar();
    const reloj = setInterval(vuelveAPreguntar, CADA_CUANTO);
    document.addEventListener("visibilitychange", vuelveAPreguntar);
    window.addEventListener("focus", vuelveAPreguntar);
    return () => {
      clearInterval(reloj);
      document.removeEventListener("visibilitychange", vuelveAPreguntar);
      window.removeEventListener("focus", vuelveAPreguntar);
    };
  }, [esperando, refresh]);

  const haz = async (fn, bien) => {
    setOcupado(true);
    try {
      await fn();
      if (bien) toast.success(bien);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "No ha salido.");
    } finally {
      setOcupado(false);
    }
  };

  const boton =
    "px-5 py-3 bg-alpha-red text-white font-mono-alpha text-[11px] uppercase tracking-widest rounded disabled:opacity-40";
  const campo =
    "w-full px-4 py-3 bg-white border border-alpha-black/15 rounded focus:border-alpha-red outline-none";

  return (
    <main className="min-h-[calc(100vh-4rem)] dvh:min-h-[calc(100svh-4rem)] bg-alpha-off flex items-center" data-testid="puerta-edad">
      <div className="w-full max-w-md mx-auto px-6 py-10">
        <div className="mb-8"><AALogo size={40} lockup="vertical" /></div>

        {!esperando ? (
          <>
            <h1 className="font-serif-display text-4xl leading-[1] tracking-tight mb-3">
              Una pregunta que no te habíamos hecho.
            </h1>
            <p className="text-alpha-black/60 mb-8 leading-snug">
              En España se decide sobre los propios datos a partir de los 14 años. Por debajo
              decide tu padre, tu madre o tu tutor, así que necesitamos saber en cuál de los
              dos casos estás.
            </p>
            <PreguntaDeEdad valor={edad} onCambia={setEdad} autoFocus />
            <button
              type="button"
              disabled={ocupado || !edad.anio || !edad.mes}
              className={`${boton} mt-6 w-full`}
              onClick={() => haz(() => declaraEdad(Number(edad.anio), Number(edad.mes)))}
              data-testid="puerta-edad-contestar"
            >
              Continuar
            </button>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl leading-[1] tracking-tight mb-3">
              Nos falta un permiso.
            </h1>
            <p className="text-alpha-black/60 mb-6 leading-snug">
              {user?.tutor_email
                ? <>Le hemos escrito a <b className="text-alpha-black">{user.tutor_email}</b>. En cuanto pulse el enlace, entras.</>
                : <>Dinos el correo de tu padre, tu madre o tu tutor y le explicamos qué es esto.</>}
            </p>

            <label className="block font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/50 mb-2">
              {user?.tutor_email ? "¿Nos equivocamos de correo?" : "Correo de tu padre, madre o tutor"}
            </label>
            <input type="email" className={campo} value={tutor} placeholder="tutor@ejemplo.com"
                   onChange={(e) => setTutor(e.target.value)} data-testid="puerta-edad-tutor" />
            <button
              type="button"
              disabled={ocupado || !tutor.includes("@")}
              className={`${boton} mt-3 w-full`}
              onClick={() => haz(() => pidePermisoTutor(tutor), "Le hemos escrito.")}
            >
              {user?.tutor_email ? "Volver a enviar" : "Enviar"}
            </button>

            {/* O UN ENLACE PARA CASA (B3.1): quien da el permiso y quien pagaría el plan son la
                misma persona, así que con él hacen las dos cosas en una pantalla. El permiso
                sigue saliendo por correo -el enlace no prueba quién lo pulsa- y pagar no lo da. */}
            <div className="mt-8">
              <p className="text-alpha-black/60 mb-3 leading-snug text-[14px]">
                ¿No te sabes su correo? Genera un enlace para que lo abra en su móvil: desde ahí
                nos dice dónde mandarle el permiso y, si quiere, ve los planes.
              </p>
              <Suspense fallback={null}><EnlaceParaCasa compacto /></Suspense>
            </div>

            {/* LA SALIDA QUE NO DEPENDE DE NADIE. Si su centro tiene las autorizaciones
                recogidas, el código de clase resuelve el permiso en el acto. Va con el
                mismo peso visual que lo de arriba a propósito: para mucho alumno ésta es la
                única de las dos que va a funcionar hoy. */}
            <div className="mt-10 pt-8 border-t border-alpha-black/10">
              <p className="text-alpha-black/60 mb-4 leading-snug">
                <b className="text-alpha-black">¿Usáis la Academia en clase?</b> Pídele a tu
                profesor el código de tu grupo. Si tu centro tiene recogidas las
                autorizaciones, entras ahora mismo sin esperar a nadie.
              </p>
              <input className={`${campo} uppercase tracking-widest font-mono-alpha`} value={codigo}
                     placeholder="CÓDIGO" onChange={(e) => setCodigo(e.target.value)}
                     data-testid="puerta-edad-codigo" />
              <button
                type="button"
                disabled={ocupado || codigo.trim().length < 4}
                className={`${boton} mt-3 w-full`}
                onClick={() => haz(async () => {
                  await entrarConCodigo(codigo);
                  navigate("/alpha", { replace: true });
                }, "Dentro.")}
              >
                Entrar con el código
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
